'use client';
import React from 'react';
import Hooks from '@benzinga/hooks';
import { WordpressPost, ContentManager } from '@benzinga/content-manager';
import { GoLinkData } from './entities';
import {
  GO_LINK_SLUG_RE,
  buildGoLinkClickData,
  extractGoLinkSlugs,
  openTrackedGoLink,
  resolveGoLinkClickAction,
} from './utils/click-tracker/goLinks';
import { SessionContext } from '@benzinga/session-context';
import { IdentityContext } from '@benzinga/identity-ui';
import { TrackingManager } from '@benzinga/tracking-manager';
import { appEnvironment, appName } from '@benzinga/utils';

export interface ClickTrackerProps {
  article?: any; // We need to replace this with an entity
  onClick?: () => void;
  post?: WordpressPost;
}

export const ClickTracker: React.FC<ClickTrackerProps> = ({ article, onClick, post }) => {
  const session = React.useContext(SessionContext);
  const { language } = React.useContext(IdentityContext);

  const isGlobalSite = React.useCallback(() => {
    return ['es', 'fr', 'it', 'ja', 'ko'].includes(language);
  }, [language]);

  const trackOutboundLink = React.useCallback(
    (anchor: HTMLAnchorElement) => {
      const event_action: string | null = anchor.getAttribute('data-action');
      const event_category: string | null = anchor.getAttribute('data-category');
      const event_label: string | null = anchor.getAttribute('data-label');

      const category = event_category || 'Outbound Links';
      const action = event_action || 'Click';
      const label = event_label || `/outgoing/${anchor.href}`;
      const button_id = action === 'Click' ? label : `${action}|${label}`;

      session.getManager(TrackingManager).trackLinkEvent('click', {
        button_id,
        button_label: anchor.nodeValue || undefined,
        button_location: 'page_click_tracker',
        button_type: 'navigation',
        link_type: category,
        link_url: anchor.href,
      });
    },
    [session],
  );

  const trackInternalLink = React.useCallback(
    (anchor: HTMLAnchorElement) => {
      const event_action: string | null = anchor.getAttribute('data-action');
      const event_category: string | null = anchor.getAttribute('data-category');

      const category = event_category || 'Internal Links';
      const action = event_action || 'Click';
      const label = `/internal/${anchor.href}`;
      const button_id = action === 'Click' ? label : `${action}|${label}`;

      const recommendation_score = anchor.getAttribute('data-recommendation-score');
      const recommendation_rank = anchor.getAttribute('data-recommendation-rank');

      session.getManager(TrackingManager).trackLinkEvent('click', {
        button_id,
        button_label: anchor.nodeValue || undefined,
        button_location: 'page_click_tracker',
        button_type: 'navigation',
        link_type: category,
        link_url: anchor.href,
        recommendation_rank: recommendation_rank != null ? Number(recommendation_rank) : undefined,
        recommendation_score: recommendation_score != null ? Number(recommendation_score) : undefined,
      });
    },
    [session],
  );

  Hooks.useEffectDidMount(() => {
    const goLinkDataBySlug: Record<string, GoLinkData> = {};
    const pendingSlugFetches = new Map<string, Promise<void>>();
    let cancelled = false;

    const mergeGoLinkData = (data: Record<string, GoLinkData> | undefined) => {
      if (!data) return;
      Object.assign(goLinkDataBySlug, data);
    };

    const fetchGoLinks = (slugs: string[]) => {
      if (slugs.length === 0) return Promise.resolve();
      return session
        .getManager(ContentManager)
        .getGoLinks(slugs)
        .then((res: { ok?: { data?: Record<string, GoLinkData> } }) => {
          if (!cancelled) mergeGoLinkData(res.ok?.data);
        })
        .catch(() => {
          // Prefetch is best-effort; click handler still opens via anchor href.
        });
    };

    const ensureGoLinkData = (slug: string) => {
      if (goLinkDataBySlug[slug] || pendingSlugFetches.has(slug)) {
        return pendingSlugFetches.get(slug) ?? Promise.resolve();
      }
      const fetchPromise = fetchGoLinks([slug]).finally(() => {
        pendingSlugFetches.delete(slug);
      });
      pendingSlugFetches.set(slug, fetchPromise);
      return fetchPromise;
    };

    const handleGoLinkClick = async (origin: HTMLAnchorElement, slug: string) => {
      await ensureGoLinkData(slug);
      if (cancelled) return;

      const goLinkData = buildGoLinkClickData(origin, goLinkDataBySlug[slug], {
        nodeId: article?.nodeId,
        postId: post?.id,
      });

      console.log('GO LINK CLICKED: ', goLinkData);

      if (resolveGoLinkClickAction(goLinkData) === 'interstitial') {
        document.dispatchEvent(
          new CustomEvent('show_unmonetized_interstitial', {
            bubbles: true,
            cancelable: true,
            detail: goLinkData,
          }),
        );
        return;
      }

      openTrackedGoLink(goLinkData, session);
      onClick?.();
    };

    const onDocumentClick = (ev: MouseEvent) => {
      if (!ev.target) return;
      const origin = (ev.target as HTMLElement).closest('a') as HTMLAnchorElement | null;
      if (!origin?.href) return;
      if (origin.hasAttribute('data-exclude-track')) return;

      const matches = origin.href.match(GO_LINK_SLUG_RE);
      if (matches) {
        // Global locales keep native navigation; US/money/proto intercept for tracking + interstitial.
        if (isGlobalSite()) return;
        ev.preventDefault();
        void handleGoLinkClick(origin, matches[1]);
        return;
      }

      if (origin.href.match('^http') && origin.href.match('benzinga.com')) {
        trackInternalLink(origin);
      } else if (origin.href.match('^http') && origin.href.indexOf(window.location.origin) === -1) {
        trackOutboundLink(origin);
      }
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('click', onDocumentClick);

      if (isGlobalSite()) return;

      const slugs = Object.keys(extractGoLinkSlugs(document.links));
      const isMoneyApp = appEnvironment().isApp(appName.money);
      const isProtoApp = appEnvironment().isApp(appName.proto);
      if (slugs.length || isMoneyApp || isProtoApp) {
        void fetchGoLinks(slugs);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('click', onDocumentClick);
    };
  });

  return null;
};
